/* eslint-disable no-underscore-dangle */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Badge, Button, Card, Modal, Spinner, Table,
} from 'react-bootstrap';
import PricingService from '../../../Services/PricingService';
import thousandSeparator from '../../../Utils/numberFormat';

const formatPrice = (n) => (n === undefined || n === null ? '—' : thousandSeparator(n));

// Plain-text rendering of an operator quote breakdown. Appended to the
// operator's comments on Apply so the operator's pay statement shows the
// math used (some operators require the full calculation in writing).
function formatQuoteAsText(quote) {
  if (!quote) return '';
  const b = quote.breakdown || {};
  const currency = quote.currency || 'EUR';
  const lines = [];
  const today = new Date().toISOString().slice(0, 10);
  lines.push(`--- Auto-quoted on ${today} ---`);
  if (quote.vehicle_category_name) lines.push(`Vehicle: ${quote.vehicle_category_name}`);

  if (b.type === 'predefined') {
    lines.push(`Fixed route price${b.predefined_route_name ? ` (${b.predefined_route_name})` : ''}: ${thousandSeparator(quote.total_price)} ${currency}`);
    return lines.join('\n');
  }

  // Distance summary
  const distParts = [];
  if (b.deadhead_to_pickup_km > 0) distParts.push(`${b.deadhead_to_pickup_km} km deadhead`);
  if (b.journey_distance_km !== undefined) distParts.push(`${b.journey_distance_km} km journey`);
  if (b.return_to_base_km > 0) distParts.push(`${b.return_to_base_km} km return`);
  if (distParts.length > 0) {
    lines.push(`Trip: ${distParts.join(' + ')} = ${b.total_distance_km ?? 0} km total`);
  }

  // Bands
  if (Array.isArray(b.band_breakdown) && b.band_breakdown.length > 0) {
    lines.push('Bands:');
    b.band_breakdown.forEach((band) => {
      const detail = [];
      if (band.flat_rate !== undefined && band.flat_rate !== null) {
        detail.push(`flat ${band.flat_rate}`);
      }
      if (band.rate !== undefined && band.rate !== null) {
        detail.push(`${band.distance_km} km × ${band.rate}`);
      }
      const detailStr = detail.length > 0 ? ` (${detail.join(' + ')})` : '';
      lines.push(`  ${band.band_name}${detailStr}: ${thousandSeparator(band.subtotal)} ${currency}`);
    });
  }

  // Multiplier
  if (b.multiplier !== undefined && b.multiplier !== 1) {
    lines.push(`Multiplier: ${b.multiplier}×${b.multiplier_source ? ` (${b.multiplier_source})` : ''} — already applied above`);
  }

  // Zone fees
  if (Array.isArray(b.zone_fees) && b.zone_fees.length > 0) {
    lines.push('Zone fees:');
    b.zone_fees.forEach((fee) => {
      lines.push(`  ${fee.service_area_name}: +${thousandSeparator(fee.fee)} ${currency}`);
    });
  }

  // Stop waiting time (multi-stop bookings)
  if (Array.isArray(b.stop_waiting) && b.stop_waiting.length > 0) {
    lines.push('Stop waiting time (min 1 h per stop):');
    b.stop_waiting.forEach((s) => {
      lines.push(`  ${s.stop_name} — ${s.duration_minutes} min → ${s.billable_hours} h × ${thousandSeparator(s.rate_per_hour)} = +${thousandSeparator(s.fee)} ${currency}`);
    });
    if (b.total_stop_waiting !== undefined) {
      lines.push(`  Total waiting: +${thousandSeparator(b.total_stop_waiting)} ${currency}`);
    }
  }

  // Warnings
  if (Array.isArray(quote.warnings) && quote.warnings.length > 0) {
    lines.push('Warnings:');
    quote.warnings.forEach((w) => lines.push(`  - ${w}`));
  }

  lines.push(`Total: ${thousandSeparator(quote.total_price)} ${currency}`);
  return lines.join('\n');
}

const STATUS_PILL = {
  ok: { variant: 'success', icon: 'fa-check-circle' },
  unconfigured: { variant: 'warning', icon: 'fa-exclamation-triangle' },
  no_profile: { variant: 'secondary', icon: 'fa-ban' },
  currency_mismatch: { variant: 'danger', icon: 'fa-money-bill' },
  error: { variant: 'danger', icon: 'fa-times-circle' },
};

// Operator quote breakdown modal — mirrors the customer-side
// PriceCalculator's modal so the cost-side breakdown reads the same way
// (Distance Summary card, Price Bands card, Zone Fees card, Total).
function QuoteBreakdownModal({
  show, onHide, quote, operatorName,
}) {
  if (!quote) return null;
  const b = quote.breakdown || {};
  const currency = quote.currency || 'EUR';
  const currencyLabel = currency === 'EUR' ? ' EUR' : ' Ft';
  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fa fa-calculator me-2" />
          Operator Quote Breakdown
          {operatorName ? `: ${operatorName}` : ''}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <Badge bg={b.type === 'predefined' ? 'success' : 'primary'} className="me-2">
            {b.type === 'predefined' ? 'Fixed Route Price' : 'Calculated Price'}
          </Badge>
          {b.predefined_route_name && (
            <span className="text-muted">{`Route: ${b.predefined_route_name}`}</span>
          )}
          {quote.vehicle_category_name && (
            <span className="text-muted ms-3">{`Vehicle: ${quote.vehicle_category_name}`}</span>
          )}
        </div>

        {b.type === 'predefined' && (
          <Card className="mb-3 border-success">
            <Card.Body>
              <strong>Fixed Price Route</strong>
              <p className="mb-0 mt-2">
                {'This route has a predefined operator price of '}
                <strong>
                  {formatPrice(quote.total_price)}
                  {currencyLabel}
                </strong>
              </p>
            </Card.Body>
          </Card>
        )}

        {b.type === 'calculated' && (
          <>
            <Card className="mb-3">
              <Card.Header className="bg-light py-2">
                <strong>
                  <i className="fa fa-road me-2" />
                  Distance Summary
                </strong>
              </Card.Header>
              <Card.Body className="py-2">
                <Table size="sm" className="mb-0">
                  <tbody>
                    {b.deadhead_to_pickup_km > 0 && (
                      <tr>
                        <td>Deadhead (base to pickup)</td>
                        <td className="text-end">{`${b.deadhead_to_pickup_km} km`}</td>
                      </tr>
                    )}
                    <tr>
                      <td>Journey distance</td>
                      <td className="text-end">{`${b.journey_distance_km ?? 0} km`}</td>
                    </tr>
                    {b.return_to_base_km > 0 && (
                      <tr>
                        <td>Return to base</td>
                        <td className="text-end">{`${b.return_to_base_km} km`}</td>
                      </tr>
                    )}
                    <tr className="table-secondary fw-bold">
                      <td>Total billable distance</td>
                      <td className="text-end">{`${b.total_distance_km ?? 0} km`}</td>
                    </tr>
                  </tbody>
                </Table>
              </Card.Body>
            </Card>

            {Array.isArray(b.band_breakdown) && b.band_breakdown.length > 0 && (
              <Card className="mb-3">
                <Card.Header className="bg-light py-2">
                  <strong>
                    <i className="fa fa-layer-group me-2" />
                    Price Bands
                  </strong>
                </Card.Header>
                <Card.Body className="py-2">
                  <Table size="sm" className="mb-0">
                    <thead>
                      <tr>
                        <th>Band</th>
                        <th className="text-center">Distance</th>
                        <th className="text-center">Rate</th>
                        <th className="text-end">{`Subtotal${currencyLabel}`}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {b.band_breakdown.map((band, idx) => (
                        <tr
                          // eslint-disable-next-line react/no-array-index-key
                          key={`${band.band_name}-${idx}`}
                        >
                          <td>{band.band_name}</td>
                          <td className="text-center">{`${band.distance_km} km`}</td>
                          <td className="text-center">
                            {(() => {
                              const hasFlat = band.flat_rate !== undefined
                                && band.flat_rate !== null
                                && band.flat_rate > 0;
                              const hasPerKm = band.rate !== undefined
                                && band.rate !== null
                                && band.rate > 0;
                              if (hasFlat && hasPerKm) {
                                return (
                                  <span>
                                    {`${formatPrice(band.flat_rate)} flat + ${band.rate}/km`}
                                  </span>
                                );
                              }
                              if (hasFlat) return <span className="text-muted">flat rate</span>;
                              if (hasPerKm) return <span>{`${band.rate}${currencyLabel}/km`}</span>;
                              return <span className="text-muted">—</span>;
                            })()}
                          </td>
                          <td className="text-end">{formatPrice(band.subtotal)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  {b.multiplier !== undefined && b.multiplier !== 1 && (
                    <div className="small text-muted mt-2">
                      {`Multiplier ${b.multiplier}× already applied to subtotals`}
                      {b.multiplier_source ? ` (from ${b.multiplier_source})` : ''}
                    </div>
                  )}
                </Card.Body>
              </Card>
            )}

            {Array.isArray(b.zone_fees) && b.zone_fees.length > 0 && (
              <Card className="mb-3">
                <Card.Header className="bg-light py-2">
                  <strong>
                    <i className="fa fa-map-marker-alt me-2" />
                    Zone Fees
                  </strong>
                </Card.Header>
                <Card.Body className="py-2">
                  <Table size="sm" className="mb-0">
                    <tbody>
                      {b.zone_fees.map((fee, idx) => (
                        <tr
                          // eslint-disable-next-line react/no-array-index-key
                          key={`${fee.service_area_name}-${idx}`}
                        >
                          <td>{fee.service_area_name}</td>
                          <td className="text-end">{`+${formatPrice(fee.fee)}${currencyLabel}`}</td>
                        </tr>
                      ))}
                      {b.total_zone_fees !== undefined && (
                        <tr className="table-secondary fw-bold">
                          <td>Total zone fees</td>
                          <td className="text-end">{`+${formatPrice(b.total_zone_fees)}${currencyLabel}`}</td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            )}
          </>
        )}

        {Array.isArray(b.stop_waiting) && b.stop_waiting.length > 0 && (
          <Card className="mb-3">
            <Card.Header className="bg-light py-2">
              <strong>
                <i className="fa fa-clock me-2" />
                Stop Waiting Time
              </strong>
              <span className="text-muted small ms-2">
                (min 1 hour per stop)
              </span>
            </Card.Header>
            <Card.Body className="py-2">
              <Table size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>Stop</th>
                    <th className="text-center">Duration</th>
                    <th className="text-center">Billable</th>
                    <th className="text-center">Rate / hr</th>
                    <th className="text-end">{`Fee${currencyLabel}`}</th>
                  </tr>
                </thead>
                <tbody>
                  {b.stop_waiting.map((s, idx) => (
                    <tr
                      // eslint-disable-next-line react/no-array-index-key
                      key={`${s.stop_name}-${idx}`}
                    >
                      <td>{s.stop_name}</td>
                      <td className="text-center">{`${s.duration_minutes} min`}</td>
                      <td className="text-center">{`${s.billable_hours} h`}</td>
                      <td className="text-center">{formatPrice(s.rate_per_hour)}</td>
                      <td className="text-end">{`+${formatPrice(s.fee)}${currencyLabel}`}</td>
                    </tr>
                  ))}
                  {b.total_stop_waiting !== undefined && (
                    <tr className="table-secondary fw-bold">
                      <td colSpan={4}>Total waiting time</td>
                      <td className="text-end">{`+${formatPrice(b.total_stop_waiting)}${currencyLabel}`}</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        )}

        {Array.isArray(quote.warnings) && quote.warnings.length > 0 && (
          <div className="alert alert-warning small">
            <strong>Warnings:</strong>
            <ul className="mb-0 ps-3">
              {quote.warnings.map((w, idx) => (
                // eslint-disable-next-line react/no-array-index-key
                <li key={idx}>{w}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="d-flex justify-content-between align-items-center mt-3 pt-2 border-top">
          <strong className="fs-5">Total</strong>
          <strong className="fs-5">
            {formatPrice(quote.total_price)}
            {currencyLabel}
          </strong>
        </div>
      </Modal.Body>
    </Modal>
  );
}

const formatMissing = (missing, lookups) => {
  if (!missing) return 'Profile is missing required configuration';
  const parts = [];
  if (missing.bands) {
    const cat = lookups.vehicleCategories.find((c) => c.Id === missing.bands);
    parts.push(`bands for ${cat?.name || missing.bands}`);
  }
  if (missing.route) {
    const route = lookups.predefinedRoutes.find((r) => r.Id === missing.route);
    parts.push(`route ${route?.name || missing.route}`);
  }
  if (missing.route_vehicle) {
    const catId = missing.route_vehicle.vehicle_category_id;
    const routeId = missing.route_vehicle.route_id;
    const cat = lookups.vehicleCategories.find((c) => c.Id === catId);
    const route = lookups.predefinedRoutes.find((r) => r.Id === routeId);
    parts.push(
      `price for ${cat?.name || catId} on route ${route?.name || routeId}`,
    );
  }
  if (missing.operator_only_route_vehicle) {
    const catId = missing.operator_only_route_vehicle.vehicle_category_id;
    const cat = lookups.vehicleCategories.find((c) => c.Id === catId);
    parts.push(`price for ${cat?.name || catId} on operator-only route`);
  }
  if (Array.isArray(missing.zones) && missing.zones.length > 0) {
    const names = missing.zones.map((id) => {
      const z = lookups.serviceAreas.find((s) => s.Id === id);
      return z?.name || id;
    });
    parts.push(`zones: ${names.join(', ')}`);
  }
  return parts.length > 0 ? `Not configured: ${parts.join('; ')}` : 'Not configured';
};

function OperatorRowQuote({
  result, operator, lookups, onApply, onShowBreakdown,
}) {
  if (!result) {
    return <Badge bg="light" text="dark">No quote</Badge>;
  }
  const meta = STATUS_PILL[result.status] || STATUS_PILL.error;
  if (result.status === 'ok') {
    const { quote } = result;
    return (
      <div className="d-flex align-items-center gap-2 flex-wrap">
        <Badge
          bg={meta.variant}
          style={{ cursor: 'pointer' }}
          onClick={() => onShowBreakdown(operator.operator_id)}
          title="Click to see how this price was calculated"
        >
          <i className={`fa ${meta.icon} me-1`} />
          {`${thousandSeparator(quote.total_price)} ${quote.currency}`}
        </Badge>
        <Button
          variant="link"
          size="sm"
          className="p-0 text-decoration-none"
          onClick={() => onShowBreakdown(operator.operator_id)}
          title="Show price breakdown"
        >
          <i className="fa fa-info-circle" />
        </Button>
        <Button
          variant="outline-success"
          size="sm"
          onClick={() => onApply(operator.operator_id, result.quote_id)}
        >
          Apply
        </Button>
      </div>
    );
  }
  // Long-form status messages use a wrappable span (Bootstrap Badge defaults
  // to white-space: nowrap which overflows the narrow assignment column).
  const statusClasses = `small d-inline-block px-2 py-1 rounded text-${meta.variant === 'warning' ? 'dark' : 'white'} bg-${meta.variant}`;
  const statusStyle = { whiteSpace: 'normal', maxWidth: '420px' };
  if (result.status === 'unconfigured') {
    return (
      <span className={statusClasses} style={statusStyle}>
        <i className={`fa ${meta.icon} me-1`} />
        {formatMissing(result.missing, lookups)}
      </span>
    );
  }
  if (result.status === 'no_profile') {
    return (
      <span className={statusClasses} style={statusStyle}>
        <i className={`fa ${meta.icon} me-1`} />
        No pricing profile configured for this operator
      </span>
    );
  }
  if (result.status === 'currency_mismatch') {
    return (
      <span className={statusClasses} style={statusStyle}>
        <i className={`fa ${meta.icon} me-1`} />
        {`Currency mismatch (profile ${result.profile_currency}, booking ${result.requested_currency})`}
      </span>
    );
  }
  return (
    <span className={statusClasses} style={statusStyle}>
      <i className={`fa ${meta.icon} me-1`} />
      {result.message || 'Quote error'}
    </span>
  );
}

function OperatorQuotePanel({
  bookingId,
  operators = [],
  onApplied,
  autoFetch = true,
}) {
  const [results, setResults] = useState({}); // operator_id -> result
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [breakdownOperatorId, setBreakdownOperatorId] = useState(null);
  const [lookups, setLookups] = useState({
    vehicleCategories: [],
    serviceAreas: [],
    predefinedRoutes: [],
  });

  // Reference data for mapping missing-IDs to names.
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      PricingService.fetchVehicleCategories(),
      PricingService.fetchServiceAreas(),
      PricingService.fetchPredefinedRoutes(),
    ])
      .then(([vcRes, saRes, prRes]) => {
        if (cancelled) return;
        setLookups({
          vehicleCategories: vcRes?.data?.data || [],
          serviceAreas: saRes?.data?.data || [],
          predefinedRoutes: prRes?.data?.data || [],
        });
      })
      .catch(() => {
        // Non-fatal — we'll fall back to IDs in display.
      });
    return () => { cancelled = true; };
  }, []);

  const operatorIds = operators
    .map((op) => op.operator_id)
    .filter(Boolean);

  const fetchQuotes = useCallback(async () => {
    if (!bookingId || operatorIds.length === 0) {
      setResults({});
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await PricingService.getOperatorQuoteBulk(bookingId, operatorIds);
      // Backend returns `{ results: [...] }`, wrapped by SuccessResponseMiddleware
      // into `{ success, data: { results: [...] } }`. Be defensive in case the
      // shape is ever a bare array (some test fixtures do this).
      const payload = response?.data?.data || response?.data;
      const list = Array.isArray(payload) ? payload : (payload?.results || []);
      const map = {};
      list.forEach((r) => {
        if (r?.operator_id) map[r.operator_id] = r;
      });
      setResults(map);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to fetch quotes');
    } finally {
      setLoading(false);
    }
  }, [bookingId, operatorIds.join('|')]);

  useEffect(() => {
    if (autoFetch) fetchQuotes();
  }, [autoFetch, fetchQuotes]);

  // Apply is a pure UI action: it fills the parent form's operator_price +
  // operator_currency fields from the quote, without persisting. The user
  // then clicks the modal's main Save button (existing flow) to write the
  // booking. No /operator-quote-apply call is made — the breakdown is
  // visible in the popover at quote time, which is sufficient audit
  // context for our needs.
  const handleApply = (operatorId, quoteId) => {
    const result = results[operatorId];
    if (!result || result.status !== 'ok' || !result.quote) return;
    if (onApplied) {
      onApplied(operatorId, {
        booking_operator: {
          operator_id: operatorId,
          price: result.quote.total_price,
          currency: result.quote.currency,
          pricing_source: 'quote',
          quote_id: quoteId,
        },
        // Plain-text breakdown to append to the operator's comments so the
        // pay statement carries the calculation. Parent decides whether/how
        // to merge with existing comments.
        comments_appendix: formatQuoteAsText(result.quote),
      });
    }
  };

  if (!bookingId) {
    return (
      <div className="alert alert-light border small">
        Save the booking first to enable operator quotes.
      </div>
    );
  }

  if (operators.length === 0) {
    return null;
  }

  return (
    <div className="operator-quote-panel border rounded p-3 mb-3 bg-light">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 className="mb-0">
          <i className="fa fa-coins me-2" />
          Operator quotes
        </h6>
        <Button variant="outline-primary" size="sm" onClick={fetchQuotes} disabled={loading}>
          {loading ? (
            <Spinner size="sm" />
          ) : (
            <>
              <i className="fa fa-sync me-1" />
              Refresh
            </>
          )}
        </Button>
      </div>

      {error && (
        <div className="alert alert-danger small py-2">{error}</div>
      )}

      {operators.map((op, idx) => {
        const result = op.operator_id ? results[op.operator_id] : null;
        const operatorLabel = op.operator?.name
          || op.operator_name
          || op.operator_id
          || `Operator ${idx + 1}`;
        const isSnapshotted = op.pricing_source === 'quote';
        const isManual = !op.pricing_source || op.pricing_source === 'manual';
        return (
          <div
            // eslint-disable-next-line react/no-array-index-key
            key={`${op.operator_id || 'pending'}-${idx}`}
            className="d-flex justify-content-between align-items-center py-2 border-bottom"
          >
            <div>
              <strong>{operatorLabel}</strong>
              {op.role && (
                <Badge bg="secondary" className="ms-2 text-uppercase">{op.role}</Badge>
              )}
              {isSnapshotted && (
                <Badge bg="info" className="ms-2">
                  {`Snapshotted${op.pricing_snapshot_at ? ` ${new Date(op.pricing_snapshot_at).toLocaleDateString()}` : ''}`}
                </Badge>
              )}
              {isManual && Number(op.price) > 0 && !result && (
                <Badge bg="light" text="dark" className="ms-2">Manual price</Badge>
              )}
            </div>
            <div className="d-flex align-items-center gap-2">
              {op.operator_id ? (
                <OperatorRowQuote
                  result={result}
                  operator={op}
                  lookups={lookups}
                  onApply={handleApply}
                  onShowBreakdown={setBreakdownOperatorId}
                />
              ) : (
                <Badge bg="light" text="dark">Select operator to fetch quote</Badge>
              )}
            </div>
          </div>
        );
      })}
      <QuoteBreakdownModal
        show={!!breakdownOperatorId}
        onHide={() => setBreakdownOperatorId(null)}
        quote={results[breakdownOperatorId]?.quote}
        operatorName={(() => {
          const op = operators.find((o) => o.operator_id === breakdownOperatorId);
          return op?.operator?.name || op?.operator_name || op?.operator_id || '';
        })()}
      />
    </div>
  );
}

export default OperatorQuotePanel;
